import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ButtonStyle } from '@constants/button/1.0';
import { AbbreviatedItemLookupType } from '@interfaces/pos-item/1.0';
import { NgIf, NgClass } from '@angular/common';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { ButtonComponent } from '../../button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-item-list',
  templateUrl: './item-list.component.html',
  styleUrls: ['./item-list.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatFormField,
    MatPrefix,
    MatInput,
    MatTable,
    MatSort,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatCheckbox,
    ButtonComponent,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
  ],
})
export class ItemListComponent implements OnInit, OnChanges {
  @Input() canUndo; // Boolean value to add undo option before save
  @Input() data: any[]; // Array data to populate list
  @Input() disableSelectionReset; // Boolean to trigger selection clear on new data;
  @Input() enableMultiSelect; // Boolean value to enable multiple selections\
  @Input() enableSelection; // Boolean value to show select column
  @Input() filterText: string; // If passed, filter search field renders above list
  @Input() hasCount; // Boolean value to show selected and length
  @Input() hasTypeColumn; // Boolean Value to show abbreviated type column
  @Input() header; // Text content for list header
  @Input() listKey: string; // Key to reference data in data source objects
  @Input() selectionKeyValue: string; // Disable rows on keys which do not have equal values
  @Input() showMapped; // Boolean value to indicate a mapped item
  @Input() showSaved; // Boolean value to show a column with a saved indicator
  @Input() typeKey: string; // Key to map type to field within item
  @Input() typeAlign: string; // [left, right] Shows the type column on the left or right side, left is default
  @Input() typeHeader = 'Type'; // Header text for the type column
  // Text content for '0 /1 {{entities selected}}', defaults to "entities selected"
  @Input() entitiesSelectedText: string;
  @Input() selectedCatalogEntryIds: string[];
  @Input() selectAllItemsByDefault; // all the checkboxes will be selected by default onInit
  @Input() disabledList: any[];
  @Input() disabledLabel: string;
  @Input() maxSelectionLimit: number;
  @Input() enableHeaderSort = false; // Click-to-sort on the name header only
  @Input() disableSelectAll = false; // disable select all checkbox if true
  @Input() preserveSelectionOnFilter = false; // keep selected rows selected when filtering them out of view

  @Output() emitSelection: EventEmitter<any> = new EventEmitter<any>();
  @Output() emitDelete: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild(MatSort, { static: true }) sort: MatSort;

  abbreviatedItemLookupType = AbbreviatedItemLookupType;
  buttonStyle = ButtonStyle; // expose enum to template
  displayColumns: string[] = ['entity'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
  selection = new SelectionModel(true, [], true);

  ngOnInit() {
    const selectedData = this.selectAllItemsByDefault ? this.data || [] : [];
    this.selection = new SelectionModel(this.enableMultiSelect, selectedData, true);

    if (this.enableSelection) {
      this.displayColumns.unshift('select');
    }

    if (this.hasTypeColumn) {
      if (this.typeAlign === 'right') {
        this.displayColumns.push('type');
      } else {
        this.displayColumns.splice(1, 0, 'type');
      }
    }

    if (this.showMapped) {
      this.displayColumns.unshift('mapped');
    }

    if (this.showSaved) {
      this.displayColumns.push('saved');
    }

    if (this.enableHeaderSort) {
      this.dataSource.sortingDataAccessor = (row, column) => this.sortValue(row, column);
      this.dataSource.sortData = (data, sort) => this.sortRows(data, sort);
    }
    this.dataSource.sort = this.sort;
    this.disableItems();
    this.dataSource.data = this.data || [];
    this.selection.changed.subscribe((newList) => this.emitSelection.emit(newList.source.selected));
  }

  disableItems() {
    if (this.disabledList && this.data) {
      this.data.forEach((element) => {
        if (element) {
          element['selectDisabled'] = this.disabledList.includes(element);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data || changes.selectedCatalogEntryIds) {
      this.disableItems();
      this.dataSource.data = this.data || [];

      if (this.disableSelectionReset) {
        if (this.selectedCatalogEntryIds !== undefined) {
          this.applySelectedCatalogEntryIds();
        }
      } else if (changes.data) {
        this.selection.clear();
      }
    }

    if (changes.disabledList) {
      this.disableItems();
    }
  }

  private applySelectedCatalogEntryIds(): void {
    const selectedIds = new Set(this.selectedCatalogEntryIds || []);
    const currentSelectedIds = new Set(this.selection.selected.map((row) => row.catalogEntryId));
    const isSameSelection =
      selectedIds.size === currentSelectedIds.size &&
      [...selectedIds].every((id) => currentSelectedIds.has(id));

    if (isSameSelection) {
      return;
    }

    this.selection.clear();
    (this.data || []).forEach((row) => {
      if (selectedIds.has(row.catalogEntryId)) {
        this.selection.select(row);
      }
    });
  }

  /* MatTable helpers */

  /**
   * checkDataForUniformKeys
   * bound to multi-select element
   * checks for multiple values for given key in all data
   *
   * @param data -- table dataSource
   * @param selectionKeyValue -- optional string, passed on list reference
   * @returns boolean
   */
  checkDataForUniformKeys(data: any[], selectionKeyValue: string): boolean {
    if (!data || data.length <= 1 || !selectionKeyValue) {
      return false;
    }

    const firstValue = data[0]?.[selectionKeyValue];
    let multipleValuesFound = false;

    for (let i = 1; i < data.length; i++) {
      const currentValue = data[i]?.[selectionKeyValue];
      if (firstValue !== currentValue) {
        multipleValuesFound = true;
        break;
      }
    }
    return multipleValuesFound;
  }

  handleDelete(item) {
    this.emitDelete.emit(item);
  }

  isAllSelected() {
    const numSelected = this.selection?.selected?.length;
    const numRows = this.dataSource?.filteredData?.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : this.dataSource.filteredData.forEach((row) => {
          if (!row?.selectDisabled) {
            this.selection.select(row);
          }
        });
  }

  private sortValue(row: any, column: string): string {
    const key = column === 'entity' ? this.listKey : column === 'type' ? this.typeKey : column;
    return row?.[key] ?? '';
  }

  private sortRows(data: any[], sort: MatSort): any[] {
    const { active, direction } = sort;
    if (!active || !direction) {
      return data;
    }

    return data.slice().sort((a, b) => {
      const result = String(this.sortValue(a, active)).localeCompare(
        String(this.sortValue(b, active)),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );
      return direction === 'asc' ? result : -result;
    });
  }

  /** Basic filter on all fields */
  filter(value) {
    this.dataSource.filter = value.trim().toLowerCase();
    if (this.preserveSelectionOnFilter) {
      return;
    }

    // apply filter to selected items as well, clearing items that do not match
    this.selection.selected.forEach((selected) => {
      const matchesFilter = this.dataSource.filterPredicate(selected, value);
      if (!matchesFilter) {
        this.selection.deselect(selected);
      }
    });
  }
}
